import React, { useState } from 'react';
import {
    Building2,
    Layers,
    Users,
    Car,
    ChevronDown,
    ChevronUp,
    Check,
    ArrowRight,
    Ruler,
    Grid3X3,
    AlertTriangle,
    Info,
    Calculator,
    Maximize2,
    Square,
    TrendingUp,
    CheckCircle,
    XCircle,
    Bike
} from 'lucide-react';

// Interfaces matching the actual API response
interface TowerConfiguration {
    towers: number;
    footprint_per_tower_sqm: number;
    tower_spacing_m: number;
    floors_per_tower: number;
    units_per_tower: number;
    units_per_floor: number;
}

interface DevelopmentScenario {
    tower_count: number;
    layout_type: string;
    feasibility: string;
    fsi_compliant: boolean;
    tower_configuration: TowerConfiguration;
    areas: {
        total_gross_built_up_sqm: number;
        total_carpet_area_sqm: number;
        total_saleable_area_sqm: number;
        carpet_area_per_floor_per_tower_sqm: number;
        efficiency_ratio: number;
    };
    units: {
        total_units: number;
        units_per_tower: number;
        units_per_floor_per_tower: number;
        unit_size_sqm: number;
        unit_size_original: { value: number; unit: string };
    };
    parking: {
        car_parking_spaces: number;
        two_wheeler_spaces: number;
        total_parking_area_sqm: number;
        car_space_area_sqm?: number;
        two_wheeler_space_area_sqm?: number;
    };
    notes: string[];
}

interface FloorBreakdown {
    floor_number: number;
    floor_type: string;
    carpet_area_sqm: number;
    circulation_area_sqm: number;
    gross_area_sqm: number;
    units: number;
}

interface SBAData {
    area_breakdown?: {
        circulation_per_floor_sqm: number;
        efficiency_ratio: number;
        gross_built_up_area_sqm: number;
        saleable_built_up_area_sqm: number;
        total_carpet_area_sqm: number;
    };
    floor_analysis?: {
        floor_height_m: number;
        max_floors_by_fsi: number;
        max_floors_by_height: number;
        permissible_floors: number;
        total_height_m: number;
        units_per_floor: number;
    };
    floor_breakdown?: FloorBreakdown[];
    height_validation?: {
        actual_height_m: number;
        applied_rule: string;
        authority_category: string;
        calculation_details: {
            height_by_road_width: number;
            height_by_zone: string;
            limiting_factor: string;
        };
        height_restricted: boolean;
        max_permissible_height_m: number;
        requested_height_m: number;
        restriction_reason: string;
    };
    layout_planning?: {
        buildable_dimensions: {
            footprint_sqm: number;
            length_m: number;
            width_m: number;
        };
        coverage_ratio: number;
        margins: {
            margin_front: number;
            margin_rear: number;
            margin_side: number;
            total_margin_area_sqm: number;
        };
        plot_dimensions: {
            area_sqm: number;
            length_m: number;
            width_m: number;
            shape_note?: string;
        };
        tower_configuration: {
            recommendation: string;
            spacing_between_towers_m: number;
            tower_footprint_sqm: number;
            towers: number;
        };
    };
    usage_recommendations?: {
        estimated_units: number;
        parking_requirement: {
            car_parking_spaces: number;
            two_wheeler_spaces: number;
            total_parking_area_sqm: number;
            car_space_area_sqm?: number;
            two_wheeler_space_area_sqm?: number;
        };
        tower_strategy: string;
        unit_size_original: { value: number; unit: string };
        unit_size_sqm: number;
        units_per_floor: number;
    };
    development_scenarios?: DevelopmentScenario[];
}

interface SBAVisualizerProps {
    data: SBAData | any; // Accept any to handle service type differences
    onTowerSelect?: (scenario: DevelopmentScenario) => void;
    onViewOnMap?: (scenario: any) => void;
}

const SBAVisualizer: React.FC<SBAVisualizerProps> = ({ data, onTowerSelect, onViewOnMap }) => {
    const [selectedScenarioIndex, setSelectedScenarioIndex] = useState<number>(0);
    const [activeTab, setActiveTab] = useState<'overview' | 'floors' | 'layout' | 'scenarios'>('overview');
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));

    const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(Math.round(num));
    const formatDecimal = (num: number, decimals: number = 2) => num.toFixed(decimals);

    const toggleSection = (section: string) => {
        const newSet = new Set(expandedSections);
        if (newSet.has(section)) {
            newSet.delete(section);
        } else {
            newSet.add(section);
        }
        setExpandedSections(newSet);
    };

    const getFeasibilityColor = (feasibility: string) => {
        switch (feasibility?.toLowerCase()) {
            case 'optimal': return { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50' };
            case 'good': return { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50' };
            case 'tight': return { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50' };
            case 'constrained': return { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50' };
            default: return { bg: 'bg-gray-500', text: 'text-gray-600', light: 'bg-gray-50' };
        }
    };

    const scenarios = data.development_scenarios || [];
    const currentScenario = scenarios[selectedScenarioIndex];

    // Render Tower 3D-style Visualization
    const renderTowerVisualization = (scenario: DevelopmentScenario) => {
        const towers = scenario.tower_count || 1;
        const floors = scenario.tower_configuration?.floors_per_tower || 7;

        return (
            <div className="flex justify-center gap-4 py-4">
                {Array.from({ length: towers }).map((_, index) => (
                    <div key={index} className="flex flex-col items-center">
                        <div
                            className="relative bg-gradient-to-t from-blue-700 via-blue-500 to-blue-400 rounded-t-lg shadow-lg transition-all duration-300"
                            style={{
                                width: `${Math.max(40, 80 - towers * 15)}px`,
                                height: `${Math.min(140, floors * 12)}px`
                            }}
                        >
                            {/* Windows grid */}
                            <div className="absolute inset-1 grid grid-cols-3 gap-0.5 p-1">
                                {Array.from({ length: Math.min(18, floors * 3) }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-yellow-200/70 rounded-sm"
                                        style={{ height: '5px' }}
                                    />
                                ))}
                            </div>
                            {/* Floor counter */}
                            <div className="absolute -right-8 top-1/2 -translate-y-1/2 text-xs text-gray-500 bg-white/80 px-1 rounded">
                                {floors}F
                            </div>
                        </div>
                        <div
                            className="bg-gradient-to-b from-blue-800 to-blue-900 rounded-b"
                            style={{ width: `${Math.max(48, 88 - towers * 15)}px`, height: '10px' }}
                        />
                        <span className="text-xs text-gray-500 mt-1 font-medium">T{index + 1}</span>
                        <span className="text-xs text-gray-400">{scenario.units?.units_per_tower || 0} units</span>
                    </div>
                ))}
            </div>
        );
    };

    // Overview Tab Content
    const renderOverviewTab = () => (
        <div className="space-y-4">
            {/* Area Breakdown Summary */}
            {data.area_breakdown && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                        <Square className="w-4 h-4" />
                        Area Breakdown
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/80 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-blue-700">
                                {formatNumber(data.area_breakdown.gross_built_up_area_sqm)}
                            </div>
                            <div className="text-xs text-blue-600">Gross Built-up (m²)</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-emerald-600">
                                {formatNumber(data.area_breakdown.saleable_built_up_area_sqm)}
                            </div>
                            <div className="text-xs text-emerald-600">Saleable (m²)</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-cyan-600">
                                {formatNumber(data.area_breakdown.total_carpet_area_sqm)}
                            </div>
                            <div className="text-xs text-cyan-600">Carpet Area (m²)</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-purple-600">
                                {Math.round(data.area_breakdown.efficiency_ratio * 100)}%
                            </div>
                            <div className="text-xs text-purple-600">Efficiency Ratio</div>
                        </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500 text-center">
                        Circulation: {formatNumber(data.area_breakdown.circulation_per_floor_sqm)} m²/floor
                    </div>
                </div>
            )}

            {/* Floor Analysis */}
            {data.floor_analysis && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-200">
                    <h4 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
                        <Layers className="w-4 h-4" />
                        Floor Analysis
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white/80 rounded-lg p-2 text-center">
                            <div className="text-xl font-bold text-orange-700">{data.floor_analysis.permissible_floors}</div>
                            <div className="text-xs text-orange-600">Floors</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-2 text-center">
                            <div className="text-xl font-bold text-amber-700">{data.floor_analysis.total_height_m}m</div>
                            <div className="text-xs text-amber-600">Total Height</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-2 text-center">
                            <div className="text-xl font-bold text-yellow-700">{data.floor_analysis.floor_height_m}m</div>
                            <div className="text-xs text-yellow-600">Per Floor</div>
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between bg-white/60 px-2 py-1 rounded">
                            <span className="text-gray-500">Max by FSI:</span>
                            <span className="font-medium">{data.floor_analysis.max_floors_by_fsi || '∞'}</span>
                        </div>
                        <div className="flex justify-between bg-white/60 px-2 py-1 rounded">
                            <span className="text-gray-500">Max by Height:</span>
                            <span className="font-medium">{data.floor_analysis.max_floors_by_height}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Height Validation */}
            {data.height_validation && (
                <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-4 border border-indigo-200">
                    <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                        <Ruler className="w-4 h-4" />
                        Height Validation
                    </h4>
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-2xl font-bold text-indigo-700">{data.height_validation.actual_height_m}m</div>
                            <div className="text-xs text-indigo-500">Actual Height</div>
                        </div>
                        <div className="text-center">
                            {data.height_validation.height_restricted ? (
                                <div className="flex items-center gap-1 text-red-600">
                                    <XCircle className="w-5 h-5" />
                                    <span className="text-sm font-medium">Restricted</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 text-emerald-600">
                                    <CheckCircle className="w-5 h-5" />
                                    <span className="text-sm font-medium">OK</span>
                                </div>
                            )}
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-gray-600">{data.height_validation.max_permissible_height_m}m</div>
                            <div className="text-xs text-gray-500">Max Permissible</div>
                        </div>
                    </div>
                    {/* Height bar */}
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="absolute h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full"
                            style={{ width: `${Math.min(100, (data.height_validation.actual_height_m / data.height_validation.max_permissible_height_m) * 100)}%` }}
                        />
                    </div>
                    <div className="mt-3 text-xs space-y-1">
                        <div className="flex justify-between text-gray-600">
                            <span>Applied Rule:</span>
                            <span className="font-medium">{data.height_validation.applied_rule}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>Authority:</span>
                            <span className="font-medium">{data.height_validation.authority_category}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                            <span>Limiting Factor:</span>
                            <span className="font-medium">{data.height_validation.calculation_details?.limiting_factor}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Usage Recommendations */}
            {data.usage_recommendations && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                    <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Usage Recommendations
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/80 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-emerald-600" />
                                <div>
                                    <div className="text-xl font-bold text-emerald-700">{data.usage_recommendations.estimated_units}</div>
                                    <div className="text-xs text-emerald-600">Estimated Units</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <Maximize2 className="w-5 h-5 text-teal-600" />
                                <div>
                                    <div className="text-xl font-bold text-teal-700">
                                        {data.usage_recommendations.unit_size_original?.value} {data.usage_recommendations.unit_size_original?.unit}
                                    </div>
                                    <div className="text-xs text-teal-600">Unit Size</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 text-sm text-emerald-700 bg-emerald-100 rounded-lg px-3 py-2">
                        <strong>Strategy:</strong> {data.usage_recommendations.tower_strategy}
                    </div>
                    {/* Parking */}
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="bg-white/60 rounded-lg p-2 text-center">
                            <Car className="w-4 h-4 mx-auto text-gray-600" />
                            <div className="font-bold text-gray-800">{data.usage_recommendations.parking_requirement?.car_parking_spaces || 0}</div>
                            <div className="text-xs text-gray-500">Cars</div>
                        </div>
                        <div className="bg-white/60 rounded-lg p-2 text-center">
                            <Bike className="w-4 h-4 mx-auto text-gray-600" />
                            <div className="font-bold text-gray-800">{data.usage_recommendations.parking_requirement?.two_wheeler_spaces || 0}</div>
                            <div className="text-xs text-gray-500">2-Wheeler</div>
                        </div>
                        <div className="bg-white/60 rounded-lg p-2 text-center">
                            <Square className="w-4 h-4 mx-auto text-gray-600" />
                            <div className="font-bold text-gray-800">{formatNumber(data.usage_recommendations.parking_requirement?.total_parking_area_sqm || 0)}</div>
                            <div className="text-xs text-gray-500">Parking m²</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // Floor Breakdown Tab
    const renderFloorsTab = () => (
        <div className="space-y-4">
            {data.floor_breakdown && data.floor_breakdown.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 border-b">
                        <h4 className="text-sm font-semibold text-gray-700">Floor-by-Floor Breakdown</h4>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Floor</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Carpet (m²)</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Circulation (m²)</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Gross (m²)</th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Units</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.floor_breakdown.map((floor: FloorBreakdown, i: number) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-gray-800">{floor.floor_type}</div>
                                            <div className="text-xs text-gray-400">Floor {floor.floor_number}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-cyan-600">
                                            {formatNumber(floor.carpet_area_sqm)}
                                        </td>
                                        <td className="px-3 py-2 text-right text-gray-600">
                                            {formatNumber(floor.circulation_area_sqm)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-blue-600">
                                            {formatNumber(floor.gross_area_sqm)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <span className="inline-flex items-center justify-center w-8 h-8 bg-emerald-100 text-emerald-700 rounded-full font-bold">
                                                {floor.units}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-100 font-semibold">
                                <tr>
                                    <td className="px-3 py-2 text-gray-700">Total</td>
                                    <td className="px-3 py-2 text-right text-cyan-700">
                                        {formatNumber(data.floor_breakdown.reduce((s: number, f: FloorBreakdown) => s + f.carpet_area_sqm, 0))}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                        {formatNumber(data.floor_breakdown.reduce((s: number, f: FloorBreakdown) => s + f.circulation_area_sqm, 0))}
                                    </td>
                                    <td className="px-3 py-2 text-right text-blue-700">
                                        {formatNumber(data.floor_breakdown.reduce((s: number, f: FloorBreakdown) => s + f.gross_area_sqm, 0))}
                                    </td>
                                    <td className="px-3 py-2 text-right text-emerald-700">
                                        {data.floor_breakdown.reduce((s: number, f: FloorBreakdown) => s + f.units, 0)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    No floor breakdown data available
                </div>
            )}
        </div>
    );

    // Layout Planning Tab
    const renderLayoutTab = () => (
        <div className="space-y-4">
            {data.layout_planning && (
                <>
                    {/* Plot Dimensions */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200">
                        <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                            <Grid3X3 className="w-4 h-4" />
                            Plot Dimensions
                        </h4>
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-indigo-700">{formatNumber(data.layout_planning.plot_dimensions.area_sqm)}</div>
                                <div className="text-xs text-indigo-600">Area (m²)</div>
                            </div>
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-blue-700">{formatDecimal(data.layout_planning.plot_dimensions.length_m)}m</div>
                                <div className="text-xs text-blue-600">Length</div>
                            </div>
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-cyan-700">{formatDecimal(data.layout_planning.plot_dimensions.width_m)}m</div>
                                <div className="text-xs text-cyan-600">Width</div>
                            </div>
                        </div>
                        {data.layout_planning.plot_dimensions.shape_note && (
                            <div className="text-xs text-indigo-500 text-center">
                                {data.layout_planning.plot_dimensions.shape_note}
                            </div>
                        )}
                    </div>

                    {/* Margins Visualization */}
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                        <h4 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                            <Square className="w-4 h-4" />
                            Required Margins
                        </h4>
                        {/* Visual margin layout */}
                        <div className="relative w-full max-w-[280px] mx-auto py-4">
                            <div className="text-center mb-2">
                                <span className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow">
                                    ↑ Front: {data.layout_planning.margins.margin_front}m
                                </span>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                                <span className="bg-blue-500 text-white px-2 py-4 rounded-lg text-xs font-bold shadow text-center" style={{ writingMode: 'vertical-rl' }}>
                                    {data.layout_planning.margins.margin_side}m
                                </span>
                                <div className="w-28 h-20 bg-emerald-100 border-4 border-emerald-500 rounded-xl flex items-center justify-center">
                                    <span className="text-emerald-700 font-bold text-sm">BUILDABLE</span>
                                </div>
                                <span className="bg-purple-500 text-white px-2 py-4 rounded-lg text-xs font-bold shadow text-center" style={{ writingMode: 'vertical-rl' }}>
                                    {data.layout_planning.margins.margin_side}m
                                </span>
                            </div>
                            <div className="text-center mt-2">
                                <span className="inline-flex items-center gap-1 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow">
                                    ↓ Rear: {data.layout_planning.margins.margin_rear}m
                                </span>
                            </div>
                        </div>
                        <div className="mt-3 text-center text-sm">
                            <span className="text-amber-700">Total margin area: </span>
                            <span className="font-bold text-amber-800">{formatNumber(data.layout_planning.margins.total_margin_area_sqm)} m²</span>
                        </div>
                    </div>

                    {/* Buildable Dimensions */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                        <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Buildable Area
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-emerald-700">{formatNumber(data.layout_planning.buildable_dimensions.footprint_sqm)}</div>
                                <div className="text-xs text-emerald-600">Footprint (m²)</div>
                            </div>
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-teal-700">{formatDecimal(data.layout_planning.buildable_dimensions.length_m)}m</div>
                                <div className="text-xs text-teal-600">Length</div>
                            </div>
                            <div className="bg-white/80 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-cyan-700">{formatDecimal(data.layout_planning.buildable_dimensions.width_m)}m</div>
                                <div className="text-xs text-cyan-600">Width</div>
                            </div>
                        </div>
                        <div className="mt-3">
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Coverage Ratio</span>
                                <span className="font-bold text-emerald-700">{(data.layout_planning.coverage_ratio * 100).toFixed(1)}%</span>
                            </div>
                            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
                                    style={{ width: `${data.layout_planning.coverage_ratio * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tower Configuration */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Tower Configuration</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Recommendation:</span>
                                <span className="font-medium text-gray-800">{data.layout_planning.tower_configuration.recommendation}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Towers:</span>
                                <span className="font-medium text-gray-800">{data.layout_planning.tower_configuration.towers}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Tower Footprint:</span>
                                <span className="font-medium text-gray-800">{formatNumber(data.layout_planning.tower_configuration.tower_footprint_sqm)} m²</span>
                            </div>
                            {data.layout_planning.tower_configuration.spacing_between_towers_m > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Tower Spacing:</span>
                                    <span className="font-medium text-gray-800">{data.layout_planning.tower_configuration.spacing_between_towers_m}m</span>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    // Scenarios Tab
    const renderScenariosTab = () => (
        <div className="space-y-4">
            {scenarios.length > 0 ? (
                <>
                    {/* Scenario selector */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {scenarios.map((scenario: DevelopmentScenario, index: number) => {
                            const colors = getFeasibilityColor(scenario.feasibility);
                            return (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setSelectedScenarioIndex(index);
                                        onTowerSelect?.(scenario);
                                    }}
                                    className={`flex-shrink-0 px-4 py-3 rounded-xl transition-all duration-200 border-2 ${selectedScenarioIndex === index
                                        ? `${colors.bg} text-white border-transparent shadow-lg`
                                        : `${colors.light} ${colors.text} border-current hover:shadow-md`
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4" />
                                        <span className="font-medium">{scenario.layout_type}</span>
                                    </div>
                                    <div className="text-xs mt-1 opacity-80">
                                        {scenario.tower_count} tower{scenario.tower_count > 1 ? 's' : ''} • {scenario.units?.total_units || 0} units
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {currentScenario && (
                        <>
                            {/* Tower Visualization */}
                            <div className="bg-gradient-to-b from-blue-50 to-white rounded-xl p-4 border border-blue-100">
                                {renderTowerVisualization(currentScenario)}
                            </div>

                            {/* Feasibility & Compliance */}
                            <div className="flex items-center justify-between">
                                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getFeasibilityColor(currentScenario.feasibility).bg} text-white`}>
                                    {currentScenario.feasibility}
                                </span>
                                {currentScenario.fsi_compliant ? (
                                    <span className="flex items-center gap-1 text-emerald-600">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="font-medium">FSI Compliant</span>
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-red-600">
                                        <XCircle className="w-5 h-5" />
                                        <span className="font-medium">Exceeds FSI</span>
                                    </span>
                                )}
                            </div>

                            {/* Scenario Stats Grid */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-blue-50 rounded-xl p-3 text-center">
                                    <Layers className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-blue-700">G + {(currentScenario.tower_configuration?.floors_per_tower || 1) - 1}</div>
                                    <div className="text-xs text-blue-600">Floors per Tower</div>
                                </div>
                                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                                    <Users className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-emerald-700">{currentScenario.units?.total_units || 0}</div>
                                    <div className="text-xs text-emerald-600">Total Units</div>
                                </div>
                                <div className="bg-cyan-50 rounded-xl p-3 text-center">
                                    <Square className="w-5 h-5 text-cyan-600 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-cyan-700">{formatNumber(currentScenario.areas?.total_saleable_area_sqm || 0)}</div>
                                    <div className="text-xs text-cyan-600">Saleable (m²)</div>
                                </div>
                                <div className="bg-purple-50 rounded-xl p-3 text-center">
                                    <Calculator className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                                    <div className="text-xl font-bold text-purple-700">{Math.round((currentScenario.areas?.efficiency_ratio || 0) * 100)}%</div>
                                    <div className="text-xs text-purple-600">Efficiency</div>
                                </div>
                            </div>

                            {/* Tower Configuration Details */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h5 className="text-sm font-semibold text-gray-700 mb-3">Tower Configuration</h5>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Footprint/Tower:</span>
                                        <span className="font-medium">{formatNumber(currentScenario.tower_configuration?.footprint_per_tower_sqm || 0)} m²</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Tower Spacing:</span>
                                        <span className="font-medium">{currentScenario.tower_configuration?.tower_spacing_m || 0}m</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Units/Floor:</span>
                                        <span className="font-medium">{currentScenario.units?.units_per_floor_per_tower || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Unit Size:</span>
                                        <span className="font-medium">{currentScenario.units?.unit_size_original?.value} {currentScenario.units?.unit_size_original?.unit}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Parking */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <Car className="w-4 h-4" />
                                    Parking Requirements
                                </h5>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="bg-white rounded-lg p-2">
                                        <div className="font-bold text-gray-800">{currentScenario.parking?.car_parking_spaces || 0}</div>
                                        <div className="text-xs text-gray-500">Car Spaces</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-2">
                                        <div className="font-bold text-gray-800">{currentScenario.parking?.two_wheeler_spaces || 0}</div>
                                        <div className="text-xs text-gray-500">2-Wheeler</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-2">
                                        <div className="font-bold text-gray-800">{formatNumber(currentScenario.parking?.total_parking_area_sqm || 0)}</div>
                                        <div className="text-xs text-gray-500">Total m²</div>
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            {currentScenario.notes && currentScenario.notes.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                    <h5 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        Notes
                                    </h5>
                                    <ul className="text-sm text-amber-700 space-y-1">
                                        {currentScenario.notes.map((note: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span>•</span>
                                                <span>{note}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* View on Map */}
                            {onViewOnMap && (
                                <button
                                    onClick={() => onViewOnMap(currentScenario)}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-colors shadow-lg"
                                >
                                    <span>Visualize on Map</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    No development scenarios available
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            {/* Tab Navigation */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
                {[
                    { id: 'overview', label: 'Overview', icon: <Info className="w-4 h-4" /> },
                    { id: 'floors', label: 'Floors', icon: <Layers className="w-4 h-4" /> },
                    { id: 'layout', label: 'Layout', icon: <Grid3X3 className="w-4 h-4" /> },
                    { id: 'scenarios', label: 'Scenarios', icon: <Building2 className="w-4 h-4" /> }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                            }`}
                    >
                        {tab.icon}
                        <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px]">
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'floors' && renderFloorsTab()}
                {activeTab === 'layout' && renderLayoutTab()}
                {activeTab === 'scenarios' && renderScenariosTab()}
            </div>
        </div>
    );
};

export default SBAVisualizer;
